import React, { useState, useEffect } from 'react';
import { useParams, useLoaderData, useNavigate } from 'react-router-dom';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import PageHeader from 'components/PageHeader';
import buildRoute from 'components/buildRoute';
import SyncWizardFooterWrapper from 'components/SyncWizardFooterWrapper';
import { validateListCutterJobName } from 'helpers/validateListCutterJobName';
import { getCsrfToken } from 'utils/csrf';
import ViewTableInBigQueryButton from 'components/bigquery/ViewTableInBigQueryButton';
import { ButtonEditableField } from 'components/editable';

function ListCutterJobDetails() {
  const { data: initialData } = useLoaderData();
  const [jobData, setJobData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const { projectId, jobId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const updateJobStatus = async () => {
      try {
        // Skip the entire process if job is already completed
        if (initialData.state === 'DONE' || (initialData.state === 'DONE' && initialData.errors)) {
          setJobData(initialData);
          setIsLoading(false);
          return;
        }
        
        setIsLoading(true);
        
        // STEP 1: Check the BigQuery job status directly
        if (initialData.bigquery_job_id) {
          const bqResponse = await fetch(`/bigquery/check-bigquery-job/?job_id=${initialData.bigquery_job_id}&project_id=${projectId}`);
          
          if (!bqResponse.ok) {
            throw new Error(`Failed to check BigQuery job status: ${bqResponse.status}`);
          }
          
          const bqJobData = await bqResponse.json();
          
          // STEP 2: Update our database with the BigQuery status
          // Only update if there's a difference in status
          if (bqJobData.state !== initialData.state || 
              (bqJobData.errors && !initialData.errors) || 
              (!bqJobData.errors && initialData.errors)) {
            
            const csrfToken = getCsrfToken();
            const updatedState = bqJobData.state === 'DONE' && bqJobData.errors ? 'FAILED' : bqJobData.state;
            
            const updateResponse = await fetch(`/list-cutter/api/jobs/${jobId}/`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              mode: 'same-origin',
              body: JSON.stringify({
                state: updatedState,
                errors: bqJobData.errors || null
              })
            });
            
            if (!updateResponse.ok) {
              throw new Error(`Failed to update job record: ${updateResponse.status}`);
            }
            
            // STEP 3: Only fetch fresh job data if we updated something
            const timestamp = new Date().getTime();
            const jobResponse = await fetch(`/list-cutter/api/jobs/${jobId}/?_=${timestamp}`);
            
            if (!jobResponse.ok) {
              throw new Error(`Failed to fetch job details: ${jobResponse.status}`);
            }
            
            const updatedJobData = await jobResponse.json();
            
            // Create a new object to ensure React detects the change
            setJobData({...updatedJobData});
          } else {
            // No change needed to database, just use the initial data
            setJobData({...initialData});
          }
        } else {
          // No BigQuery job ID, just use the initial data
          setJobData({...initialData});
        }
      } catch (error) {
        console.error('Error updating job status:', error);
        // In case of error, show the initial data
        setJobData({...initialData});
      } finally {
        setIsLoading(false);
      }
    };

    updateJobStatus();
  }, [jobId, projectId, initialData]);

  const handleRename = async (newName) => {
    try {
      const validation = await validateListCutterJobName(newName, jobData.name);
      
      if (!validation.isValid) {
        throw new Error(validation.errorMessage);
      }
      
      const csrftoken = getCsrfToken();
      const response = await fetch(`/list-cutter/api/jobs/${jobId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({
          name: newName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to rename job');
      }

      const updatedData = await response.json();
      if (!updatedData || Object.keys(updatedData).length === 0) {
        setJobData(prevData => ({
          ...prevData,
          name: newName
        }));
      } else {
        setJobData(updatedData);
      }
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  const nameTextSize = '1.75rem';

  const NameField = () => (
    <Box sx={{ 
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)', 
      pb: 1,
      mb: 0
    }}>
      <ButtonEditableField 
        value={jobData.name || 'Unnamed Job'}
        onSave={handleRename}
        displayStyles={{
          fontSize: nameTextSize,
          fontWeight: 500,
          wordBreak: 'break-word'
        }}
        editButtonText="Rename"
        confirmButtonText="Save"
        inputProps={{
          size: "medium",
          InputProps: { 
            sx: { 
              fontSize: nameTextSize,
              fontWeight: 500
            } 
          }
        }}
        placeholder="Enter job name"
        vertical={true}
        editButtonProps={{
          sx: { 
            alignSelf: 'flex-start',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              '& .MuiSvgIcon-root': {
                color: 'primary.dark'
              }
            }
          }
        }}
      />
    </Box>
  );

  // Format the errors for display
  const formatErrors = (errors) => (
    <Box>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          mt: 1,
          mb: 0.5
        }}
      >
        Error:
      </Typography>
      <Box 
        sx={{ 
          backgroundColor: 'error.lighter',
          border: 1,
          borderColor: 'error.main',
          borderRadius: 1,
          p: 2
        }}
      >
        <Typography 
          component="pre" 
          sx={{ 
            color: 'error.dark',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            m: 0
          }}
        >
          {Array.isArray(errors) 
            ? errors.map(error => error.message).join('\n')
            : JSON.stringify(errors, null, 2)
          }
        </Typography>
      </Box>
    </Box>
  );

  const baseFields = [
    {
      label: 'Status',
      key: 'state',
      format: (value) => (
        <Box>
          <span>{value === 'DONE' && jobData.errors ? 'FAILED' : value || 'Not started'}</span>
          {/* Display errors directly under the status if they exist */}
          {jobData.errors && formatErrors(jobData.errors)}
        </Box>
      )
    },
    {
      label: 'Results Table',
      key: 'target_table_ref',
      format: (value) => value || `${jobData.target_dataset}.${jobData.target_table}`
    },
    {
      label: 'Source Table',
      key: 'source_table_ref',
      format: (value) => value || `${jobData.source_dataset}.${jobData.source_table}`
    },
    {
      label: 'Created At',
      key: 'created',
      format: (value) => new Date(value).toLocaleString()
    },
    {
      label: 'Selected Columns',
      key: 'selected_columns',
      format: (value) => Array.isArray(value) ? value.join(', ') : value
    },
    {
      label: 'Filters',
      key: 'filters',
      format: (value) => value ? JSON.stringify(value, null, 2) : 'No filters'
    },
    {
      label: 'Row Limit',
      key: 'limit',
      format: (value) => value ? value : 'No limit'
    },
    {
      label: 'Top Rows Ordered By',
      key: 'order_by_conditions',
      format: (value) => value ? JSON.stringify(value, null, 2) : 'No ordering conditions'
    },
    {
      label: 'SQL',
      key: 'generated_sql',
      format: (value) => value ? (
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          p: 3, 
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto'
        }}>
          {value}
        </Box>
      ) : 'No SQL'
    }
  ];

  const fields = baseFields.filter(field => field.key !== 'name');

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="List Cutter Job Details"
          subtitle="View details about your list cutter job"
        />
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 8,
          minHeight: '300px'
        }}>
          <CircularProgress size={40} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Loading job details...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Retrieving the latest status for your List Cutter job
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="List Cutter Job Details"
        subtitle="View details about your list cutter job"
      />
      
      <Box sx={{ 
        bgcolor: 'background.paper', 
        p: 3, 
        borderRadius: 1,
        mb: 2,
        boxShadow: 1
      }}>
        <NameField />
        {/* only show the button to view the list in BQ if the job is DONE and there are no errors */}
        {jobData.state === "DONE" && !jobData.errors && (
          <Box sx={{ my: 3 }}>
            <ViewTableInBigQueryButton
              projectId={projectId}
              datasetId={jobData.target_dataset}
              tableId={jobData.target_table}
              buttonText="View in BigQuery"
              variant="outlined"
              sx={{ mt: 1, mb: -2 }}
            />
          </Box>
        )}
        
        <Box sx={{ mt: -2 }}>
          <Box sx={{ mt: 4 }}>
            {fields.map((field, index) => (
              <Box 
                key={field.key} 
                sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  ...(field.key === 'state' ? { pt: 2 } : {})
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {field.label}
                </Typography>
                <Box sx={{ ml: 0 }}>
                  {field.format ? field.format(jobData[field.key]) : jobData[field.key]}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "space-between" }}>
        <Button 
          color="inherit" 
          onClick={() => navigate(`/projects/${projectId}/list-cutter`)}
        >
          Back to Jobs
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate(`/projects/${projectId}/list-cutter/new?template=${jobData.id}`)}
        >
          Create Another Job Like This
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );
}

const routeArgs = {
  element: <ListCutterJobDetails />,
  loader: async ({ params }) => {
    try {
      const response = await fetch(`/list-cutter/api/jobs/${params.jobId}/`);
      if (!response.ok) {
        throw new Error('Failed to fetch job details');
      }
      const data = await response.json();
      return { data };
    } catch (error) {
      return { error: error.message };
    }
  },
};

export default buildRoute(routeArgs);